import {
    Alert,
    Box,
    Icon,
    IconButton,
    MobileStepper,
    Typography,
    useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { RootState } from "../../../slices/Store";
import { toJalaliDate } from "../../../utils/date";
import ProjectMobileImageGallery from "../../projects/dialogs/ProjectMobileImageGallery";

// Import placeholders
import {
    DefaultDarkProjectImagePath,
    DefaultProjectImagePath,
} from "../../../constants/ProjectConstants";

// Import Swiper styles
import "swiper/css/bundle";
import { PhotoDto } from "../../../api/psc";

const SwiperHandleButton = styled(IconButton)(() => ({
    position: "absolute",
    zIndex: "10",
    top: "40%",
    background: "rgba(0,0,0,0.1)",
    backdropFilter: "blur(20px)",
}));

const SwiperHandleIcon = styled(Icon)(() => ({
    fontSize: "30px",
    color: "white",
}));

const SwiperMobileStepper = styled(MobileStepper)(() => ({
    zIndex: "10",
    position: "absolute",
    bottom: 0,
    borderRadius: "10px",
    borderStartStartRadius: "0",
    borderStartEndRadius: "0",
    left: -1,
    right: -1,
    background: "rgba(0,0,0,0.1)",
    height: "35px",
    border: "none",
    justifyContent: "center",
    backdropFilter: "blur(20px)",
}));

const SwiperWapper = styled(Box)(() => ({
    position: "relative",
    marginBottom: "4px",
}));

const SwiperDateIndicator = styled(Box)(() => ({
    position: "absolute",
    right: 16,
    top: 16,
    background: "rgba(0,0,0,0.2)",
    padding: 8,
    backdropFilter: "blur(20px)",
}));

const renderSliders = (
    photos: PhotoDto[],
    toggleShowGallery: () => void,
    projectPlaceHolder: string
) => {
    return (
        <>
            {photos.map((photo) => (
                <SwiperSlide key={photo.imageId} onClick={toggleShowGallery}>
                    <div
                        style={{
                            backgroundImage: `url(${projectPlaceHolder})`,
                            backgroundPosition: "center",
                            minHeight: "250px",
                            borderRadius: "12px",
                            overflow: "hidden",
                        }}
                    >
                        <img
                            src={photo.imageUrl}
                            width="100%"
                            height="auto"
                            style={{ borderRadius: "12px" }}
                        />

                        <SwiperDateIndicator>
                            <Typography color={"white"}>
                                {toJalaliDate(photo.shootingDate)}
                            </Typography>
                        </SwiperDateIndicator>
                    </div>
                </SwiperSlide>
            ))}
        </>
    );
};

export default function () {
    const theme = useTheme();
    const swiperRef = useRef<SwiperRef>(null);

    const projectDetailState = useSelector(
        (state: RootState) => state.projectDetail
    );

    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
    const [showGallery, setShowGallery] = useState(false);

    const projectDetail = projectDetailState.projectDetail;

    //Place Holders
    const projectPlaceHolder =
        theme.palette.mode === "light"
            ? DefaultProjectImagePath
            : DefaultDarkProjectImagePath;

    const photos = projectDetail.photos.slice(0, 25);

    const constructorViewInfo =
        projectDetailState.projectDetail?.constructorViewInfo;

    if (projectDetail.photos.length === 0) {
        return <></>;
    }

    const handleNext = () => {
        if (selectedPhotoIndex === photos.length - 1)
            return toggleShowGallery();
        swiperRef.current.swiper.slideNext();
    };

    const handleBack = () => {
        if (selectedPhotoIndex === 0) return toggleShowGallery();
        swiperRef.current.swiper.slidePrev();
    };

    const incrementSelectedPhotoIndex = () => {
        setSelectedPhotoIndex((prevActiveStep) =>
            Math.min(prevActiveStep + 1, photos.length - 1)
        );
    };
    const decrementSelectedPhotoIndex = () => {
        setSelectedPhotoIndex((prevActiveStep) =>
            Math.max(prevActiveStep - 1, 0)
        );
    };

    const toggleShowGallery = () => {
        setShowGallery(!showGallery);
    };


    return (
        <>
            <SwiperWapper>
                {showGallery && (
                    <ProjectMobileImageGallery
                        photos={photos}
                        toggle={toggleShowGallery}
                    />
                )}

                <Swiper
                    onSlideNextTransitionStart={incrementSelectedPhotoIndex}
                    onSlidePrevTransitionStart={decrementSelectedPhotoIndex}
                    ref={swiperRef}
                >
                    {renderSliders(
                        photos,
                        toggleShowGallery,
                        projectPlaceHolder
                    )}
                </Swiper>

                <SwiperMobileStepper
                    steps={photos.length}
                    position="static"
                    activeStep={selectedPhotoIndex}
                    nextButton={<></>}
                    backButton={<></>}
                />

                <SwiperHandleButton onClick={handleBack} sx={{ left: 16 }}>
                    <SwiperHandleIcon>chevron_right</SwiperHandleIcon>
                </SwiperHandleButton>

                <SwiperHandleButton onClick={handleNext} sx={{ right: 16 }}>
                    <SwiperHandleIcon>chevron_left</SwiperHandleIcon>
                </SwiperHandleButton>
            </SwiperWapper>

            {projectDetailState.projectDetail.isVisited && (
                <Alert
                    sx={{ my: 2 }}
                    variant="standard"
                    severity="info"
                    icon={<Icon>person</Icon>}
                >
                    {`این پروژه توسط
                     ${constructorViewInfo.user?.displayName}
                     در تاریخ
                     ${toJalaliDate(constructorViewInfo.visitDate)}
                     دیده شده است.`}
                </Alert>
            )}
        </>
    );
}
